import React, { useState, useEffect, useCallback } from 'react';
import BrandMark from './BrandMark';
import { useNavigate } from 'react-router-dom';
import { getAdmin, logout } from '../auth';
import { celeryAPI } from '../api';
import './CeleryTasks.css';

// Страница «Задачи»: очередь Celery, активные задачи, Finder-прогоны.
// Позволяет вылечить затор (как инцидент 2026-07-09) прямо из админки:
// убить зависшую задачу, отменить прогон, очистить очередь.
// Вёрстка переиспользует классы Dashboard.css (dashboard-header, metrics-grid,
// section-title, loading-screen) — они в общем бандле, стиль единый.
const CeleryTasks = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const load = useCallback(async () => {
    try {
      const resp = await celeryAPI.getOverview();
      setData(resp.data);
      setLastUpdate(new Date());
    } catch (err) {
      setMsg({ type: 'err', text: 'Не удалось загрузить данные Celery' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setAdmin(getAdmin());
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [load]);

  const fmtRuntime = (sec) => {
    if (sec === null || sec === undefined) return '—';
    if (sec < 60) return `${sec}с`;
    if (sec < 3600) return `${Math.floor(sec / 60)}м ${sec % 60}с`;
    return `${Math.floor(sec / 3600)}ч ${Math.floor((sec % 3600) / 60)}м`;
  };

  const doAction = async (fn, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    setMsg(null);
    try {
      const resp = await fn();
      setMsg({ type: 'ok', text: resp.data.message });
      await load();
    } catch (err) {
      setMsg({ type: 'err', text: err.response?.data?.detail || 'Ошибка операции' });
    } finally {
      setBusy(false);
    }
  };

  const revokeTask = (t) => doAction(
    () => celeryAPI.revoke(t.id, true),
    `Убить задачу ${t.name}?\nПроцесс будет остановлен, слот воркера освободится.`
  );

  const cancelRun = (r) => doAction(
    () => celeryAPI.cancelFinderRun(r.id),
    `Отменить Finder-прогон #${r.id} (${r.symbol})?`
  );

  const purge = () => doAction(
    () => celeryAPI.purge(),
    `ОЧИСТИТЬ ВСЮ ОЧЕРЕДЬ (${data?.queue?.length || 0} задач)?\n\nПериодические задачи вернутся сами (beat пришлёт новые), но разовые (ручные сканы, запуски прогонов) будут потеряны. Использовать только при заторе.`
  );

  const stuckCount = (data?.active_tasks || []).filter(t => t.is_stuck || t.is_duplicate).length;

  return (
    <div className="dashboard">
      {/* Header — как на дашборде */}
      <header className="dashboard-header">
        <div className="header-left">
          <div className="logo">
            <BrandMark size={28} className="logo-icon" />
            <span className="logo-text">Balancify</span>
            <span className="logo-badge">Admin</span>
          </div>
          <span className="ct-page-title">Задачи Celery</span>
        </div>
        <div className="header-right">
          {lastUpdate && (
            <span className="last-update">
              Обновлено: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <button onClick={() => navigate('/')} className="btn-logout" title="Вернуться на дашборд">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Дашборд
          </button>
          {admin && (
            <div className="admin-badge">
              <span className="admin-avatar">{admin.username?.charAt(0).toUpperCase()}</span>
              <div className="admin-info">
                <span className="admin-name">{admin.username}</span>
                <span className="admin-role">{admin.role}</span>
              </div>
            </div>
          )}
          <button onClick={logout} className="btn-logout">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Выйти
          </button>
        </div>
      </header>

      <main className="dashboard-content">
        {msg && (
          <div className={`ct-msg ${msg.type} fade-in`}>
            {msg.text}
            <button className="ct-msg-close" onClick={() => setMsg(null)}>×</button>
          </div>
        )}

        {loading && !data && (
          <div className="loading-screen">
            <div className="loading-spinner"></div>
            <p>Загрузка задач...</p>
          </div>
        )}

        {data && (
          <>
            {/* KPI */}
            <section className="section fade-in" style={{ animationDelay: '0.05s' }}>
              <div className="metrics-grid">
                <div className={`metric-card ${data.queue.length > 500 ? 'gradient-red' : 'gradient-blue'}`}>
                  <div className="metric-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                      <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                    </svg>
                  </div>
                  <div className="metric-content">
                    <span className="metric-value">{data.queue.length}</span>
                    <span className="metric-label">Задач в очереди</span>
                  </div>
                </div>

                <div className="metric-card gradient-purple">
                  <div className="metric-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/>
                      <line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/>
                      <line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/>
                      <line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/>
                      <line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/>
                    </svg>
                  </div>
                  <div className="metric-content">
                    <span className="metric-value">{data.workers.length}</span>
                    <span className="metric-label">Воркеров онлайн</span>
                  </div>
                </div>

                <div className="metric-card gradient-green">
                  <div className="metric-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="5 3 19 12 5 21 5 3"/>
                    </svg>
                  </div>
                  <div className="metric-content">
                    <span className="metric-value">{data.active_tasks.length}</span>
                    <span className="metric-label">Выполняется сейчас</span>
                  </div>
                </div>

                <div className={`metric-card ${stuckCount > 0 ? 'gradient-red' : 'gradient-orange'}`}>
                  <div className="metric-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  </div>
                  <div className="metric-content">
                    <span className="metric-value">{stuckCount}</span>
                    <span className="metric-label">Подозрительных (&gt;1ч / дубликат)</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Активные задачи */}
            <section className="section fade-in" style={{ animationDelay: '0.1s' }}>
              <h2 className="section-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
                Выполняются сейчас
              </h2>
              <div className="ct-card">
                <p className="ct-hint">
                  «Дубликат» — одна и та же задача занимает несколько слотов (передоставка брокером; так выглядел
                  инцидент 09.07). Долгая задача (&gt;1ч) — не всегда проблема: Finder-прогон идёт ~17 часов.
                  Но если при этом растёт очередь — останавливайте.
                </p>
                {data.active_tasks.length === 0 ? (
                  <div className="ct-empty">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    Слоты свободны — воркеры ждут задач
                  </div>
                ) : (
                  <div className="ct-table-wrap">
                    <table className="ct-table">
                      <thead>
                        <tr><th>Задача</th><th>Аргументы</th><th>Воркер</th><th>Работает</th><th>Статус</th><th></th></tr>
                      </thead>
                      <tbody>
                        {data.active_tasks.map((t, i) => (
                          <tr key={`${t.id}-${i}`} className={t.is_stuck || t.is_duplicate ? 'ct-row-warn' : ''}>
                            <td className="ct-task-name">{t.name?.replace('analytical_tasks.', '').replace('tasks.', '')}</td>
                            <td className="ct-args">{t.kwargs !== '{}' ? t.kwargs : t.args}</td>
                            <td className="ct-worker">{t.worker?.split('@')[1]?.slice(0, 8)}</td>
                            <td className="ct-runtime">{fmtRuntime(t.runtime_sec)}</td>
                            <td>
                              {t.is_duplicate && <span className="ct-badge ct-badge-dup">Дубликат</span>}
                              {t.is_stuck && <span className="ct-badge ct-badge-stuck">&gt;1ч</span>}
                              {!t.is_stuck && !t.is_duplicate && <span className="ct-badge ct-badge-ok">OK</span>}
                            </td>
                            <td className="ct-actions">
                              <button className="ct-btn-danger" onClick={() => revokeTask(t)} disabled={busy}>
                                Остановить
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>

            {/* Finder-прогоны */}
            {data.finder_runs.length > 0 && (
              <section className="section fade-in" style={{ animationDelay: '0.15s' }}>
                <h2 className="section-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  Активные Finder-прогоны
                </h2>
                <div className="ct-card">
                  <p className="ct-hint">
                    Отмена ставит cancel-флаг на 24 часа и останавливает активные копии задачи — даже
                    передоставленная брокером копия завершится сразу после подхвата.
                  </p>
                  <div className="ct-table-wrap">
                    <table className="ct-table">
                      <thead>
                        <tr><th>#</th><th>Монета</th><th>Статус</th><th>Прогресс</th><th></th></tr>
                      </thead>
                      <tbody>
                        {data.finder_runs.map(r => (
                          <tr key={r.id}>
                            <td className="ct-worker">{r.id}</td>
                            <td className="ct-task-name">{r.symbol}</td>
                            <td><span className="ct-badge ct-badge-ok">{r.status}</span></td>
                            <td className="ct-args">{r.progress}% {r.message ? `· ${r.message}` : ''}</td>
                            <td className="ct-actions">
                              <button className="ct-btn-danger" onClick={() => cancelRun(r)} disabled={busy}>
                                Отменить прогон
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

            {/* Очередь */}
            <section className="section fade-in" style={{ animationDelay: '0.2s' }}>
              <h2 className="section-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                Очередь ({data.queue.length})
              </h2>
              <div className="ct-card">
                {data.queue.breakdown.length === 0 ? (
                  <div className="ct-empty">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    Очередь пуста — все задачи выполняются вовремя
                  </div>
                ) : (
                  <div className="ct-table-wrap">
                    <table className="ct-table">
                      <thead><tr><th>Задача</th><th>В очереди</th></tr></thead>
                      <tbody>
                        {data.queue.breakdown.map(b => (
                          <tr key={b.task}>
                            <td className="ct-task-name">{b.task.replace('analytical_tasks.', '').replace('tasks.', '')}</td>
                            <td>{b.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {admin?.role === 'super_admin' && data.queue.length > 0 && (
                  <div className="ct-purge-row">
                    <button className="ct-btn-danger ct-btn-purge" onClick={purge} disabled={busy}>
                      Очистить очередь полностью
                    </button>
                    <span className="ct-hint-inline">только при заторе; периодические задачи вернутся сами</span>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default CeleryTasks;
